import { get, post } from ".";
import { EhrVendor, getEhrVendor } from "Utils/EhrVendorMap";
import { EhrDomains } from "Constant/Ehr";

/**
 * Per-section and practice-wide sync against ehr-integration.
 *
 * Both vendors expose the same per-domain shape — {@code
 * /patient/{id}/<domain>/sync} — so the only thing that varies is the base
 * segment and, for eCW, the practice location in the path. The vendor comes
 * from {@link PracticeEhrVendorMap}, so the caller only supplies the patient's
 * practice.
 *
 * The id is CareVitality's internal patient uuid (redox_patient.id), not the
 * EHR's id — that is what the sync routes expect on both sides.
 */

export enum EhrSyncDomain {
  DEMOGRAPHICS = "demographics",
  COVERAGE = "insurance",
  CONDITIONS = "conditions",
  ALLERGIES = "allergies",
  IMMUNIZATIONS = "immunizations",
  MEDICATIONS = "medications",
  LAB_RESULTS = "lab-results",
  VITALS = "vitals",
  DOCUMENTS = "documents",
  PATIENT_CASES = "patient-cases",
}

/** Section labels, used in button tooltips and the bulk screen. */
export const DomainLabel: Record<EhrSyncDomain, string> = {
  [EhrSyncDomain.DEMOGRAPHICS]: "Demographics",
  [EhrSyncDomain.COVERAGE]: "Insurance",
  [EhrSyncDomain.CONDITIONS]: "Problems",
  [EhrSyncDomain.ALLERGIES]: "Allergies",
  [EhrSyncDomain.IMMUNIZATIONS]: "Immunizations",
  [EhrSyncDomain.MEDICATIONS]: "Medications",
  [EhrSyncDomain.LAB_RESULTS]: "Lab Results",
  [EhrSyncDomain.VITALS]: "Vitals",
  [EhrSyncDomain.DOCUMENTS]: "Documents",
  [EhrSyncDomain.PATIENT_CASES]: "Patient Cases",
};

/**
 * Athena has no vitals sync, and demographics re-sync is eCW-only (it goes
 * through the bulk/onboarding path there).
 */
const UNSUPPORTED: Record<EhrVendor, EhrSyncDomain[]> = {
  [EhrVendor.ECW]: [EhrSyncDomain.PATIENT_CASES],
  [EhrVendor.ATHENA]: [EhrSyncDomain.VITALS, EhrSyncDomain.DEMOGRAPHICS],
};

export const isDomainSupported: (
  practiceLocationId: number | null | undefined,
  domain: EhrSyncDomain
) => boolean = (practiceLocationId, domain) => {
  const vendor = getEhrVendor(practiceLocationId);
  if (!vendor) return false;
  return !UNSUPPORTED[vendor].includes(domain);
};

/** Domains the practice's vendor actually supports, in display order. */
export const supportedDomains: (
  practiceLocationId: number | null | undefined
) => EhrSyncDomain[] = (practiceLocationId) =>
  Object.values(EhrSyncDomain).filter((d) =>
    isDomainSupported(practiceLocationId, d)
  );

/**
 * The syncing machine's clock, sent with every sync so "last synced" reads back
 * as the time this user saw when they pressed the button rather than the
 * server's. Sent as an instant, so it renders correctly wherever it is read.
 */
const clientNow: () => string = () => new Date().toISOString();

export type SectionSyncTimes = Record<string, string | null>;

/**
 * Key under which the newest sync across all sections arrives — the time the
 * Sync All button shows.
 */
export const ALL_SECTIONS = "all";

/**
 * Last sync time for every section of one patient, in one call.
 *
 * Vendor-neutral: this reads what was stored, so unlike the sync routes it does
 * not care which EHR the practice uses. A section that has never synced comes
 * back null, and {@link ALL_SECTIONS} holds the newest of them — which is why
 * syncing any single section moves the Sync All time with it.
 */
/**
 * The one domain the two EHR endpoints spell differently.
 *
 * `capabilities` calls it `insurance`, which is what {@link EhrSyncDomain} uses because that string
 * also builds the sync URL. `sync-times` calls it `coverage`, after Section.COVERAGE on the server.
 * Reading the response with the capabilities spelling therefore returned undefined, and the Insurance
 * button rendered "Never synced" permanently -- including immediately after a successful sync.
 *
 * Mapped on read rather than renaming either side: `coverage` is the server's internal vocabulary for
 * this domain in several places (EcwClinicalSyncService, EcwBulkCoverageSyncService,
 * AthenaPatientParallelSyncServiceImpl), and `insurance` is what the sync route needs. Normalising here
 * keeps both contracts intact and puts the translation in the one place that needs it.
 */
const SYNC_TIME_KEY: Partial<Record<string, string>> = {
  [EhrSyncDomain.COVERAGE]: "coverage",
  // Same disagreement, second instance: the sync route and capabilities call this
  // `cases`, while `sync-times` calls it `patient-cases` after Section.PATIENT_CASES.
  // Missing it left the Patient Cases button reading "Never synced" even after a good
  // sync. Athena-only, so eCW never showed the fault.
  [EhrDomains.CASES]: "patient-cases",
};

/** The sync-times key for a domain, which is the domain itself unless the two endpoints disagree. */
export const syncTimeKeyFor: (domain: string) => string = (domain) =>
  SYNC_TIME_KEY[domain] ?? domain;

const fetchSyncTimes: (patientId: string) => Promise<SectionSyncTimes> = async (
  patientId
) => {
  const res: any = await get({
    subUrl: `ehr-api/ehr/patients/${patientId}/sync-times`,
    isEhr: true,
    // A 403 here is the ANSWER to "may this user see EHR data?", not a failure. This fires on
    // mount from every patient screen, so for a role without the ehr read permission -- Trainer,
    // before it was granted 5058 -- it raised a "Forbidden" toast for a call the user never made
    // (CV-473). An empty map already renders as "nothing synced", which is the correct view.
    silentStatuses: [403],
  });
  return res?.success && res?.data ? res.data : {};
};

/**
 * One request per patient, shared by every Sync button on the screen.
 *
 * Nine section buttons plus Sync All all want the same map, and they are spread
 * across nine unrelated components with no common parent to fetch it in.
 * Caching the promise here is what keeps that one call instead of ten,
 * without threading
 * a prop through every screen that hosts a button.
 */
let cachedPatientId: string | null = null;
let pendingSyncTimes: Promise<SectionSyncTimes> | null = null;
const syncTimesListeners = new Set<() => void>();
const syncCompletedListeners = new Set<(domain: string) => void>();
const notifySyncCompleted = (domain: string): void => {
  syncCompletedListeners.forEach((listener) => listener(domain));
};
const onSyncCompleted = (listener: (domain: string) => void): (() => void) => {
  syncCompletedListeners.add(listener);
  return () => { syncCompletedListeners.delete(listener); };
};
export type EhrCapabilities = {
  vendor?: string;
  enabled?: boolean;
  syncDomains?: { domain: string; enabled: boolean; reason?: string }[];
};
const capabilityRequests = new Map<string, Promise<EhrCapabilities>>();

const getCapabilities = (patientId: string): Promise<EhrCapabilities> => {
  if (!capabilityRequests.has(patientId)) {
    capabilityRequests.set(patientId, Promise.resolve(get({
      subUrl: `ehr-api/ehr/patients/${patientId}/capabilities`,
      isEhr: true,
      // Same as fetchSyncTimes: a denied capability probe means "no". The {} below already makes
      // canSync false, so the Sync buttons stay hidden instead of erroring.
      silentStatuses: [403],
    })).then((res: any) => res?.success ? res.data || {} : {}));
  }
  return capabilityRequests.get(patientId)!;
};

const getSyncTimes: (patientId: string) => Promise<SectionSyncTimes> = (
  patientId
) => {
  if (patientId !== cachedPatientId) {
    cachedPatientId = patientId;
    pendingSyncTimes = null;
  }
  if (!pendingSyncTimes) pendingSyncTimes = fetchSyncTimes(patientId);
  return pendingSyncTimes;
};

/**
 * Rereads the times and tells every mounted button about it.
 *
 * Called after any successful sync, because a section sync also moves the Sync
 * All time — that button has to notice a click it did not handle.
 */
const refreshSyncTimes: (patientId: string) => void = (patientId) => {
  cachedPatientId = patientId;
  pendingSyncTimes = fetchSyncTimes(patientId);
  syncTimesListeners.forEach((listener) => listener());
};

/** Subscribes to {@link refreshSyncTimes}. Returns the unsubscribe. */
const onSyncTimesChanged: (listener: () => void) => () => void = (listener) => {
  syncTimesListeners.add(listener);
  return () => {
    syncTimesListeners.delete(listener);
  };
};

/**
 * "14 Aug 2025, 4:12 pm" in the reader's own locale and zone.
 *
 * The stored instant came from the machine that synced, so whoever pressed the
 * button reads back the time their own clock showed.
 */
export const formatSyncTime: (iso: string | null | undefined) => string = (
  iso
) => {
  if (!iso) return "Never synced";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "Never synced";
  return when.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const buildSubUrl: (
  vendor: EhrVendor,
  practiceLocationId: number,
  patientId: string,
  domain: EhrSyncDomain
) => string = (vendor, practiceLocationId, patientId, domain) => {
  if (vendor === EhrVendor.ECW) {
    return (
      `ehr-ecw/practice-location/${practiceLocationId}` +
      `/api/patient/${patientId}/${domain}/sync`
    );
  }
  return `ehr-athena/patient/${patientId}/${domain}/sync`;
};

/**
 * Syncs one section for one patient. Resolves the vendor from the practice
 * location.
 */
const syncDomain: (
  practiceLocationId: number | null | undefined,
  patientId: string,
  domain: EhrSyncDomain
) => any = (practiceLocationId, patientId, domain) => {
  if (!patientId) return Promise.resolve({ success: false, message: "Patient is required." });
  return post({
    subUrl: `ehr-api/ehr/patients/${patientId}/sync/${domain}`,
    params: { syncedAt: clientNow() },
    isEhr: true,
  });
};

/**
 * Syncs every domain for one patient in a single call.
 *
 * Both vendors orchestrate this server-side — one request, not nine — so a
 * partial failure is reported per domain in the response rather than losing the
 * whole run.
 */
const syncAll: (
  practiceLocationId: number | null | undefined,
  patientId: string
) => any = (practiceLocationId, patientId) => {
  if (!patientId) return Promise.resolve({ success: false, message: "Patient is required." });
  return post({
    subUrl: `ehr-api/ehr/patients/${patientId}/sync/all`,
    params: { syncedAt: clientNow() },
    isEhr: true,
  });
};

export type EcwBulkOptions = {
  maxPatients?: number;
  prefixes?: string;
  concurrency?: number;
  /** Inclusive ISO dates for the eCW encounter-window sweep. */
  fromDate?: string;
  toDate?: string;
};

export type EcwBulkCoverageOptions = {
  maxPatients?: number;
  concurrency?: number;
  onlyMissing?: boolean;
};

export type BulkExportStartOptions = {
  groupId: string;
  types?: string;
  since?: string;
};

const bulkExportGroups = (practiceLocationId: number | null | undefined): any => {
  const vendor = getEhrVendor(practiceLocationId);
  if (!vendor || practiceLocationId == null) {
    return Promise.resolve({ success: false, message: "Select an EHR-enabled practice." });
  }
  const subUrl = vendor === EhrVendor.ECW
    ? `ehr-ecw/practice-location/${practiceLocationId}/api/bulk-export`
    : `ehr-athena/practice-location/${practiceLocationId}/bulk-export/groups`;
  return get({ subUrl, isEhr: true, acceptRaw: vendor === EhrVendor.ECW, dedupe: true });
};

const startBulkExport = (
  practiceLocationId: number | null | undefined,
  options: BulkExportStartOptions
): any => {
  const vendor = getEhrVendor(practiceLocationId);
  if (!vendor || practiceLocationId == null || !options.groupId?.trim()) {
    return Promise.resolve({ success: false, message: "Practice and group are required." });
  }
  if (vendor === EhrVendor.ECW) {
    return post({
      subUrl: `ehr-ecw/practice-location/${practiceLocationId}/api/bulk-export`,
      params: { groupId: options.groupId, types: options.types || undefined, since: options.since || undefined },
      isEhr: true,
      acceptRaw: true,
    });
  }
  return post({
    subUrl: `ehr-athena/practice-location/${practiceLocationId}/bulk-export/start`,
    data: { groupId: options.groupId },
    isEhr: true,
  });
};

const bulkExportJobStatus = (
  practiceLocationId: number | null | undefined,
  jobId: string | number
): any => {
  const vendor = getEhrVendor(practiceLocationId);
  if (!vendor || practiceLocationId == null || jobId === "") {
    return Promise.resolve({ success: false, message: "Practice and job id are required." });
  }
  const subUrl = vendor === EhrVendor.ECW
    ? `ehr-ecw/practice-location/${practiceLocationId}/api/bulk-export/jobs/${encodeURIComponent(jobId)}/status`
    : `ehr-athena/practice-location/${practiceLocationId}/bulk-export/jobs/${encodeURIComponent(jobId)}/status`;
  return get({ subUrl, isEhr: true, acceptRaw: vendor === EhrVendor.ECW, dedupe: true });
};

/**
 * Practice-wide sync.
 *
 * The two vendors are not symmetric here and cannot be made so: Athena grants
 * Bulk Data access, so it starts an asynchronous FHIR {@code $export} job. eCW
 * grants this client none, so it sweeps {@code Patient?name=} prefixes
 * synchronously and returns counts when the sweep finishes.
 */
const bulkSync: (
  practiceLocationId: number | null | undefined,
  options?: EcwBulkOptions
) => any = (practiceLocationId, options) => {
  const vendor = getEhrVendor(practiceLocationId);
  if (
    !vendor ||
    practiceLocationId === null ||
    practiceLocationId === undefined
  ) {
    return Promise.resolve({
      success: false,
      message: "This practice is not configured for an EHR.",
    });
  }
  if (vendor === EhrVendor.ECW) {
    // Encounter-driven, not name-prefix driven. The prefix sweep returned every
    // patient in the practice; this returns the patients actually seen in the
    // requested window, each with their profile. Different question, so the
    // dates are required rather than optional.
    if (!options?.fromDate || !options?.toDate) {
      return Promise.resolve({
        success: false,
        message: "From date and to date are required for an eCW bulk sync.",
      });
    }
    if (options.fromDate > options.toDate) {
      return Promise.resolve({
        success: false,
        message: "From date must not be after to date.",
      });
    }
    return get({
      subUrl:
        `ehr-ecw/practice-location/${practiceLocationId}` +
        "/api/encounter-window/patients",
      params: { fromDate: options.fromDate, toDate: options.toDate },
      isEhr: true,
    });
  }
  return post({
    subUrl:
      `ehr-athena/practice-location/${practiceLocationId}` +
      "/bulk-export/start",
    data: {},
    isEhr: true,
  });
};

/**
 * eCW-only dry run: sweeps and counts patients without writing to
 * redox_patient.
 */
const bulkPreview: (
  practiceLocationId: number | null | undefined,
  options?: EcwBulkOptions
) => any = (practiceLocationId, options) => {
  if (getEhrVendor(practiceLocationId) !== EhrVendor.ECW) {
    return Promise.resolve({
      success: false,
      message: "Preview is only available for eCW practices.",
    });
  }
  const params: any = { hydrate: false };
  if (options?.maxPatients) params.maxPatients = options.maxPatients;
  if (options?.prefixes) params.prefixes = options.prefixes;
  if (options?.concurrency) params.concurrency = options.concurrency;
  return get({
    subUrl: `ehr-ecw/practice-location/${practiceLocationId}/api/patients/bulk`,
    params,
    isEhr: true,
  });
};

/**
 * eCW-only coverage sync for patients already onboarded into CareVitality.
 * Defaults to patients that have no stored insurance, avoiding unnecessary
 * calls for patients whose coverage has already been imported.
 */
const bulkCoverageSync: (
  practiceLocationId: number | null | undefined,
  options?: EcwBulkCoverageOptions
) => any = (practiceLocationId, options) => {
  if (getEhrVendor(practiceLocationId) !== EhrVendor.ECW) {
    return Promise.resolve({
      success: false,
      message: "Bulk insurance sync is only available for eCW practices.",
    });
  }
  const params: any = { onlyMissing: options?.onlyMissing ?? true };
  if (options?.maxPatients) params.maxPatients = options.maxPatients;
  if (options?.concurrency) params.concurrency = options.concurrency;
  return post({
    subUrl:
      `ehr-ecw/practice-location/${practiceLocationId}` +
      "/api/patients/bulk/coverage/sync",
    params,
    isEhr: true,
  });
};

/**
 * eCW encounters for one provider over a date range.
 *
 * eCW-only: this is the one route that finds patients we do not already hold an
 * id for, so it is how a practice is discovered by provider rather than by name
 * prefix. Both dates are mandatory — eCW rejects a provider id on its own — and
 * ehr-integration splits the span into the <=15-day windows eCW enforces, so the
 * caller can pass any range.
 */
const encountersByProvider: (
  practiceLocationId: number | null | undefined,
  providerId: string,
  start: string,
  end: string
) => any = (practiceLocationId, providerId, start, end) => {
  if (getEhrVendor(practiceLocationId) !== EhrVendor.ECW) {
    return Promise.resolve({
      success: false,
      message: "Encounter search by provider is only available for eCW practices.",
    });
  }
  if (!providerId?.trim() || !start || !end) {
    return Promise.resolve({
      success: false,
      message: "Provider id, start date and end date are all required.",
    });
  }
  return get({
    subUrl:
      `ehr-ecw/practice-location/${practiceLocationId}` +
      "/api/encounters/by-provider",
    params: { providerId: providerId.trim(), start, end },
    isEhr: true,
  });
};

export default {
  syncDomain,
  syncAll,
  getSyncTimes,
  getCapabilities,
  refreshSyncTimes,
  onSyncTimesChanged,
  notifySyncCompleted,
  onSyncCompleted,
  bulkSync,
  bulkPreview,
  bulkCoverageSync,
  encountersByProvider,
  bulkExportGroups,
  startBulkExport,
  bulkExportJobStatus,
  isDomainSupported,
  supportedDomains,
};
